import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  BodyWrapper,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
} from '@components';
import {
  authorsQuery,
  blogCategoriesQuery,
  postDeleteMutation,
  postMutation,
  postQuery,
  siteQuery,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import PostForm from '../components/PostForm/PostForm.jsx';
import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';
import { useDatamodel } from '@hooks';
export const Post = () => {
  const { t, i18n } = useTranslation();
  const { postID, siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // get post
  const {
    data: postData,
    isPending: postIsPending,
    isError,
  } = useQuery(postQuery(postID));

  // get author
  const { data: authorsData, isPending: authorsIsPending } = useQuery(
    authorsQuery({
      limit: 100,
      order_by: 'name',
      site_id: siteID,
    }),
  );

  // get categories
  const { data: blogCategoriesData, isPending: blogCategoriesIsPending } =
    useQuery(
      blogCategoriesQuery({
        limit: 100,
        site_id: siteID,
      }),
    );

  const {
    datamodelData,
    isPending: isCustomBlogsPending,
    relatedDatamodels,
  } = useDatamodel('blog_custom_fields');

  // save post
  const mutation = useMutation(
    postMutation(postID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['posts', 'list'] });
        // invalidate the detail for revisits
        queryClient.invalidateQueries({
          queryKey: ['posts', 'detail', postID],
        });
      },
    }),
  );

  // delete post
  const deleteMutation = useMutation(
    postDeleteMutation(postID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['posts', 'list'] });
        // navigate to the list view
        navigate(`/dashboard/sites/${siteID}/blogs`);
      },
    }),
  );

  const modifyData = (data) => {
    // modify data before its passed to the form
    return {
      ...data,
      authors: data.authors.map((author) => author.id),
      categories: data.categories
        .filter((c) => !c.is_post_type)
        .map((c) => c.id),
      post_type_category: data.categories.find((c) => c.is_post_type)?.id,
    };
  };

  const isPending =
    siteIsPending ||
    postIsPending ||
    authorsIsPending ||
    blogCategoriesIsPending ||
    isCustomBlogsPending;

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <Link to={`/dashboard/sites/${siteID}/blogs`}>
                {t('Blog posts')}
              </Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              {postIsPending && <span>...</span>}
              {postData && <span>{postData.title}</span>}
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            {isPending && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}
            {siteData &&
              postData &&
              authorsData &&
              blogCategoriesData &&
              !isPending &&
              !isError && (
                <>
                  <PostForm
                    postData={modifyData(postData)}
                    authorsData={authorsData}
                    categoriesData={blogCategoriesData}
                    mutation={mutation}
                    disabled={mutation.isPending || deleteMutation.isPending}
                    siteID={siteID}
                    siteData={siteData}
                    datamodelData={datamodelData}
                    relatedDatamodels={relatedDatamodels}
                  />
                  <div className="sb-message-action-section">
                    <ActionObject
                      onAction={() => deleteMutation.mutate()}
                      loading={deleteMutation.isPending}
                      actionName="Delete"
                      actionVerb="Deleting"
                      label="Delete post"
                      note="This post will be permanently deleted"
                    />
                  </div>
                </>
              )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
